import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Download, Save, User, Phone, AlertTriangle, QrCode } from 'lucide-react';

export const QRProfilePage = () => {
  const { horseId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [firstContact, setFirstContact] = useState({ name: '', phone: '' });
  const [selectedRiderId, setSelectedRiderId] = useState('');
  const qrRef = useRef(null);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [horseId, token]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/qr/${horseId}/profile`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setFirstContact({
          name: result.first_contact.name || '',
          phone: result.first_contact.phone || ''
        });
        setSelectedRiderId(result.selected_rider_id || '');
      } else {
        toast.error('Failed to load QR profile');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load QR profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/qr/${horseId}/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          first_contact_name: firstContact.name,
          first_contact_phone: firstContact.phone,
          selected_rider_id: selectedRiderId || null
        })
      });
      if (response.ok) {
        toast.success('QR Profile saved');
        fetchData();
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrRef.current) {
      toast.error('QR code not found');
      return;
    }
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) {
      toast.error('QR code SVG not found');
      return;
    }
    
    try {
      // Create canvas with larger size for better quality
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 400; // Larger for better quality
      canvas.width = size;
      canvas.height = size;
      
      // Get SVG data
      const svgData = new XMLSerializer().serializeToString(svg);
      
      // Create a blob from SVG
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      img.onload = () => {
        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Draw QR centered
        const padding = 40;
        ctx.drawImage(img, padding, padding, size - (padding * 2), size - (padding * 2));
        
        // Create download link
        canvas.toBlob((blob) => {
          if (blob) {
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `qr_${data?.horse?.stable_name?.replace(/\s+/g, '_') || 'horse'}.jpg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(downloadUrl);
            toast.success('QR code downloaded!');
          }
        }, 'image/jpeg', 0.95);
        
        URL.revokeObjectURL(url);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        toast.error('Failed to generate QR image');
      };
      
      img.src = url;
    } catch (error) {
      console.error('QR download error:', error);
      toast.error('Failed to download QR code');
    }
  };

  const publicUrl = data?.qr_code_id ? `${window.location.origin}/qr/${data.qr_code_id}` : '';
  const selectedRider = data?.riders?.find(r => r.rider_id === selectedRiderId);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Horse not found</p>
          <Link to="/horses">
            <Button variant="outline" className="mt-4">Back to Horses</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="qr-profile-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={`/horses/${horseId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              QR-ID Profile
            </h1>
            <p className="text-muted-foreground">{data.horse.stable_name}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Code Card */}
          <Card className="border-0 shadow-card bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-forest" />
                QR Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div 
                ref={qrRef}
                className="bg-white p-4 rounded-lg border flex items-center justify-center"
              >
                <QRCodeSVG 
                  value={publicUrl}
                  size={180}
                  level="M"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center break-all">
                {publicUrl}
              </p>
              <Button 
                onClick={handleDownloadQR} 
                className="w-full bg-forest hover:bg-forest-dark gap-2"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4" />
                Download QR Code (JPEG)
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Scan this QR code to view horse and rider information
              </p>
            </CardContent>
          </Card>

          {/* Horse ID Profile */}
          <Card className="border-0 shadow-card bg-white">
            <CardHeader>
              <CardTitle>Horse ID Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-medium">{data.horse.registered_name} ({data.horse.stable_name})</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Breed:</span>
                  <p className="font-medium">{data.horse.breed || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Sex:</span>
                  <p className="font-medium">{data.horse.sex || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Colour:</span>
                  <p className="font-medium">{data.horse.colour || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">DOB:</span>
                  <p className="font-medium">{data.horse.dob || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Height:</span>
                  <p className="font-medium">{data.horse.height || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Microchip:</span>
                  <p className="font-medium">{data.horse.microchip || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Registration:</span>
                  <p className="font-medium">{data.horse.registration_number || '-'}</p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <span className="text-muted-foreground">Owner:</span>
                <p className="font-medium">{data.horse.owner || '-'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* First Contact Section */}
        <Card className="border-0 shadow-card bg-white border-l-4 border-l-forest">
          <CardHeader>
            <CardTitle className="text-lg font-bold">First Contact</CardTitle>
            <p className="text-sm text-muted-foreground">Primary contact for emergencies</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" /> Name
                </Label>
                <Input
                  value={firstContact.name}
                  onChange={(e) => setFirstContact({ ...firstContact, name: e.target.value })}
                  placeholder="Contact name"
                  data-testid="first-contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Contact Number
                </Label>
                <Input
                  value={firstContact.phone}
                  onChange={(e) => setFirstContact({ ...firstContact, phone: e.target.value })}
                  placeholder="Phone number"
                  data-testid="first-contact-phone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rider Profile Section */}
        <Card className="border-0 shadow-card bg-white">
          <CardHeader>
            <CardTitle>Rider Profile</CardTitle>
            <p className="text-sm text-muted-foreground">Select a rider to display on the QR scan page</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Rider</Label>
              <Select value={selectedRiderId || "none"} onValueChange={(val) => setSelectedRiderId(val === "none" ? "" : val)}>
                <SelectTrigger data-testid="select-rider">
                  <SelectValue placeholder="Choose a rider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {data.riders.map(rider => (
                    <SelectItem key={rider.rider_id} value={rider.rider_id}>
                      {rider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedRider && (
              <div className="bg-muted/30 p-4 rounded-lg space-y-2 text-sm">
                <p><strong>Name:</strong> {selectedRider.name}</p>
                {selectedRider.contact_number && (
                  <p><strong>Phone:</strong> {selectedRider.contact_number}</p>
                )}
                {selectedRider.birth_date && (
                  <p><strong>Date of Birth:</strong> {selectedRider.birth_date}</p>
                )}
                {selectedRider.emergency_contact_name && (
                  <div className="pt-2 border-t mt-2">
                    <p className="flex items-center gap-1 text-destructive font-medium mb-1">
                      <AlertTriangle className="w-4 h-4" /> Emergency Contact
                    </p>
                    <p><strong>Name:</strong> {selectedRider.emergency_contact_name}</p>
                    <p><strong>Phone:</strong> {selectedRider.emergency_contact_number || '-'}</p>
                  </div>
                )}
                {selectedRider.emergency_medical_notes && (
                  <div className="pt-2">
                    <p><strong>Medical Notes:</strong></p>
                    <p className="text-muted-foreground">{selectedRider.emergency_medical_notes}</p>
                  </div>
                )}
              </div>
            )}

            {data.riders.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No rider profiles found. <Link to="/rider-profiles" className="text-forest hover:underline">Add a rider profile</Link>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            className="bg-forest hover:bg-forest-dark gap-2 px-8"
            disabled={saving}
            data-testid="save-qr-profile-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};
